import { PermissionCode, type ManagedDepartment } from '@gb-ticket/shared-types';
import { Loader2Icon, MoreHorizontalIcon, PlusIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { hasPermission } from '@/features/auth/permissions';
import { useMe } from '@/features/auth/use-me';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import { adminPanelClassName, adminTableFrameClassName } from '@/platform/features/admin-panel';
import {
  DepartmentFormDialog,
  type DepartmentFormValues,
} from '@/platform/features/organisation/department-form-dialog';
import { useDialogInvalidationHold } from '@/platform/lib/use-invalidation-hold';
import {
  useCreateDepartment,
  useDeleteDepartment,
  useDepartments,
  useUpdateDepartment,
} from '@/platform/features/organisation/use-organisation';
import { ApiRequestError } from '@/lib/api';
import { cn } from '@/lib/utils';

type DialogState = { mode: 'create' } | { mode: 'edit'; values: DepartmentFormValues } | null;

type DeactivateState = {
  id: string;
  name: string;
  userCount: number;
  teamCount: number;
  assetCount: number;
} | null;

type DeleteState = {
  id: string;
  name: string;
} | null;

function toFormValues(item: ManagedDepartment): DepartmentFormValues {
  return {
    id: item.id,
    name: item.name,
    slug: item.slug,
    isActive: item.isActive,
  };
}

export function DepartmentsPage() {
  const me = useMe();
  const canWrite = hasPermission(me.data, PermissionCode.USERS_MANAGE);
  const [searchInput, setSearchInput] = useState('');
  const [q, setQ] = useState<string | undefined>();
  const departments = useDepartments({ q, includeInactive: true });
  const createDepartment = useCreateDepartment();
  const updateDepartment = useUpdateDepartment();
  const deleteDepartment = useDeleteDepartment();
  const [dialog, setDialog] = useState<DialogState>(null);
  const [deactivate, setDeactivate] = useState<DeactivateState>(null);
  const [pendingDelete, setPendingDelete] = useState<DeleteState>(null);
  useDialogInvalidationHold(dialog !== null || deactivate !== null || pendingDelete !== null);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setQ(searchInput.trim() || undefined);
    }, 300);
    return () => window.clearTimeout(handle);
  }, [searchInput]);

  const busy = createDepartment.isPending || updateDepartment.isPending;

  async function handleFormSubmit(payload: {
    create?: Parameters<typeof createDepartment.mutateAsync>[0];
    update?: Parameters<typeof updateDepartment.mutateAsync>[0]['body'];
  }) {
    try {
      if (dialog?.mode === 'create' && payload.create) {
        await createDepartment.mutateAsync(payload.create);
        toast.success('Abteilung angelegt.');
        setDialog(null);
        return;
      }
      if (dialog?.mode === 'edit' && payload.update && dialog.values.id) {
        await updateDepartment.mutateAsync({ id: dialog.values.id, body: payload.update });
        toast.success(
          payload.update.isActive === false ? 'Abteilung deaktiviert.' : 'Abteilung gespeichert.',
        );
        setDialog(null);
      }
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Die Abteilung konnte nicht gespeichert werden.',
      );
    }
  }

  async function confirmDeactivate() {
    if (!deactivate) {
      return;
    }
    try {
      await updateDepartment.mutateAsync({ id: deactivate.id, body: { isActive: false } });
      toast.success('Abteilung deaktiviert.');
      setDeactivate(null);
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Die Abteilung konnte nicht deaktiviert werden.',
      );
    }
  }

  async function reactivate(item: ManagedDepartment) {
    try {
      await updateDepartment.mutateAsync({ id: item.id, body: { isActive: true } });
      toast.success('Abteilung reaktiviert.');
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Die Abteilung konnte nicht reaktiviert werden.',
      );
    }
  }

  async function confirmDelete() {
    if (!pendingDelete) {
      return;
    }
    try {
      await deleteDepartment.mutateAsync(pendingDelete.id);
      toast.success(`„${pendingDelete.name}“ wurde endgültig gelöscht.`);
      setPendingDelete(null);
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Die Abteilung konnte nicht gelöscht werden.',
      );
    }
  }

  function usageHint(state: DeactivateState): string {
    if (!state) {
      return '';
    }
    const parts: string[] = [];
    if (state.userCount > 0) {
      parts.push(`${state.userCount} Benutzer`);
    }
    if (state.teamCount > 0) {
      parts.push(`${state.teamCount} Team${state.teamCount === 1 ? '' : 's'}`);
    }
    if (state.assetCount > 0) {
      parts.push(`${state.assetCount} Asset${state.assetCount === 1 ? '' : 's'}`);
    }
    if (parts.length === 0) {
      return `„${state.name}“ wird deaktiviert und erscheint nicht mehr in neuen Zuweisungen.`;
    }
    return `„${state.name}“ wird deaktiviert. Betroffen: ${parts.join(', ')}. Bestehende Zuordnungen bleiben erhalten.`;
  }

  return (
    <section className="space-y-6">
      <PlatformPageHeader
        title="Abteilungen"
        description="Firmenorganisation für Benutzer, Inventar und Auswertungen."
        action={
          canWrite ? (
            <Button type="button" onClick={() => setDialog({ mode: 'create' })}>
              <PlusIcon className="size-4" aria-hidden />
              Abteilung hinzufügen
            </Button>
          ) : undefined
        }
      />

      <div className={`${adminPanelClassName} max-w-sm`}>
        <Input
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          placeholder="Name oder Kurzname suchen …"
          aria-label="Abteilungen suchen"
        />
      </div>

      {departments.isPending && (
        <p className="flex items-center gap-2 text-muted-foreground">
          <Loader2Icon className="size-4 animate-spin" aria-hidden />
          Abteilungen werden geladen …
        </p>
      )}

      {departments.isError && (
        <p role="alert" className="text-sm text-destructive">
          {departments.error instanceof ApiRequestError
            ? departments.error.message
            : 'Die Abteilungen konnten nicht geladen werden.'}
        </p>
      )}

      {departments.data && (
        <div className={adminTableFrameClassName}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Kurzname</TableHead>
                <TableHead className="text-right">Benutzer</TableHead>
                <TableHead className="text-right">Teams</TableHead>
                <TableHead className="text-right">Assets</TableHead>
                <TableHead>Status</TableHead>
                {canWrite && (
                  <TableHead className="w-12">
                    <span className="sr-only">Aktionen</span>
                  </TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {departments.data.items.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={canWrite ? 7 : 6}
                    className="text-center text-muted-foreground"
                  >
                    Keine Abteilungen gefunden.
                  </TableCell>
                </TableRow>
              ) : (
                departments.data.items.map((item) => (
                  <TableRow key={item.id} className={cn(!item.isActive && 'opacity-70')}>
                    <TableCell className="font-medium">{item.name}</TableCell>
                    <TableCell className="font-mono text-sm">{item.slug}</TableCell>
                    <TableCell className="text-right tabular-nums">{item.userCount}</TableCell>
                    <TableCell className="text-right tabular-nums">{item.teamCount}</TableCell>
                    <TableCell className="text-right tabular-nums">{item.assetCount}</TableCell>
                    <TableCell>
                      {item.isActive ? (
                        <Badge variant="outline">Aktiv</Badge>
                      ) : (
                        <Badge variant="destructive">Deaktiviert</Badge>
                      )}
                    </TableCell>
                    {canWrite && (
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon-sm"
                              aria-label={`${item.name}: Aktionen`}
                            >
                              <MoreHorizontalIcon className="size-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onSelect={() =>
                                setDialog({ mode: 'edit', values: toFormValues(item) })
                              }
                            >
                              Bearbeiten
                            </DropdownMenuItem>
                            {item.isActive ? (
                              <DropdownMenuItem
                                variant="destructive"
                                onSelect={() =>
                                  setDeactivate({
                                    id: item.id,
                                    name: item.name,
                                    userCount: item.userCount,
                                    teamCount: item.teamCount,
                                    assetCount: item.assetCount,
                                  })
                                }
                              >
                                Deaktivieren
                              </DropdownMenuItem>
                            ) : (
                              <>
                                <DropdownMenuItem onSelect={() => void reactivate(item)}>
                                  Reaktivieren
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  variant="destructive"
                                  disabled={!item.canDelete}
                                  title={
                                    item.canDelete
                                      ? undefined
                                      : 'Löschen erst möglich, wenn keine Benutzer, Teams oder Assets zugeordnet sind.'
                                  }
                                  onSelect={() =>
                                    setPendingDelete({ id: item.id, name: item.name })
                                  }
                                >
                                  Endgültig löschen
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      )}

      <DepartmentFormDialog
        open={dialog !== null}
        mode={dialog?.mode ?? 'create'}
        initial={dialog?.mode === 'edit' ? dialog.values : null}
        busy={busy}
        onOpenChange={(open) => {
          if (!open) {
            setDialog(null);
          }
        }}
        onSubmit={handleFormSubmit}
      />

      <AlertDialog
        open={deactivate !== null}
        onOpenChange={(open) => {
          if (!open) {
            setDeactivate(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Abteilung deaktivieren?</AlertDialogTitle>
            <AlertDialogDescription>{usageHint(deactivate)}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={updateDepartment.isPending}>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              disabled={updateDepartment.isPending}
              onClick={(event) => {
                event.preventDefault();
                void confirmDeactivate();
              }}
            >
              {updateDepartment.isPending ? (
                <>
                  <Loader2Icon className="size-4 animate-spin" aria-hidden />
                  Wird deaktiviert …
                </>
              ) : (
                'Deaktivieren'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => {
          if (!open) {
            setPendingDelete(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Abteilung endgültig löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingDelete ? `„${pendingDelete.name}“ wird unwiderruflich entfernt.` : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteDepartment.isPending}>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              disabled={deleteDepartment.isPending}
              onClick={(event) => {
                event.preventDefault();
                void confirmDelete();
              }}
            >
              {deleteDepartment.isPending ? (
                <>
                  <Loader2Icon className="size-4 animate-spin" aria-hidden />
                  Wird gelöscht …
                </>
              ) : (
                'Endgültig löschen'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
